import { z } from 'zod'
import { RoleName } from '~/types/api/user/role'

export const emmFlowAccessRoles: RoleName[] = [
    RoleName.developer,
    RoleName.webdept,
    RoleName.businessdev,
]

export const emmFlowCreateRoles: RoleName[] = [RoleName.developer]

/** Campaign flow version: `v` followed by one or more digits, e.g. `v1` or `v20`. */
export const campaignFlowVersionPattern = /^v\d+$/

export const campaignFlowVersionSchema = z
    .string()
    .trim()
    .regex(campaignFlowVersionPattern, 'Version must start with v followed by digits only, for example v1 or v20')

export function isValidCampaignFlowVersion(version: string): boolean {
    return campaignFlowVersionPattern.test(version.trim())
}

export function campaignFlowVersionNumberFromValue(version: string): string {
    return version.trim().replace(/^v/i, '').replace(/\D/g, '')
}

export function campaignFlowVersionFromNumber(digits: string): string {
    return `v${digits.trim()}`
}

export function campaignFlowVersionNumberError(digits: string): string | undefined {
    const value = digits.trim()
    if (!value) return 'Version is required'
    if (!/^\d+$/.test(value)) return 'Version must be a number'
    return undefined
}

/** Flow code key: 1–6 uppercase A–Z letters, e.g. `TESTDD` or `CONSDK`. */
export const campaignFlowCodeKeyPattern = /^[A-Z]{1,6}$/

export const campaignFlowCodeKeySchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(
        z.string().regex(
            campaignFlowCodeKeyPattern,
            'Flow code key must be 1–6 capital letters with no other characters',
        ),
    )

export function campaignFlowCodeKeyError(key: string): string | undefined {
    const value = key.trim()
    if (!value) return 'Flow code key is required'
    if (!campaignFlowCodeKeyPattern.test(value)) {
        return 'Flow code key must be at most 6 characters and only capital letters'
    }
    return undefined
}

export function emmFlowRequestErrorMessage(err: unknown, fallback: string): string {
    const e = err as {
        statusMessage?: string
        message?: string
        data?: { message?: string; statusMessage?: string } | string
    }
    if (typeof e.data === 'string' && e.data.trim()) return e.data.trim()
    if (typeof e.data === 'object' && e.data) {
        const fromBody = e.data.statusMessage || e.data.message
        if (fromBody?.trim()) return fromBody.trim()
    }
    if (e.statusMessage?.trim()) return e.statusMessage.trim()
    if (e.message?.trim() && !e.message.startsWith('[GET]') && !e.message.startsWith('[POST]')) {
        return e.message.trim()
    }
    return fallback
}

const coerceId = z.coerce.number().finite()

export const emmCampaignFlowCodeSchema = z.object({
    campaign_flow_code_key: z.string().min(1),
    campaign_flow_code: z.string().min(1),
    version: z.coerce.string().min(1),
})

export const emmFlowLinkedTagSchema = z.object({
    tagId: coerceId,
    tag: z.string(),
    campaignFlowCode: emmCampaignFlowCodeSchema.nullable().optional(),
})

export const emmFlowTagToLinkSchema = z.object({
    tagId: coerceId,
    tag: z.string(),
    campaignFlowCode: emmCampaignFlowCodeSchema.nullable().optional(),
})

export const emmCampaignFlowCodesResponseSchema = z.object({
    statusCode: z.coerce.number(),
    message: z.string(),
    existingFlowCodeLinkedTags: z.array(emmFlowLinkedTagSchema).nullable(),
    tagsToBeLinked: z.array(emmFlowTagToLinkSchema).nullable(),
    existingCampaignFlowCodes: z.array(emmCampaignFlowCodeSchema).nullable(),
    success: z.boolean(),
})

export type EmmCampaignFlowCode = z.infer<typeof emmCampaignFlowCodeSchema>
export type EmmFlowLinkedTag = z.infer<typeof emmFlowLinkedTagSchema>
export type EmmCampaignFlowCodesResponse = z.infer<typeof emmCampaignFlowCodesResponseSchema>

export const editCampaignFlowCodeRequestSchema = z.object({
    clientId: z.number().int().positive(),
    emmCampaignFlowCode: z.object({
        originalCampaignFlowCodeKey: z.string().min(1),
        version: campaignFlowVersionSchema,
    }),
})

export type EditCampaignFlowCodeRequest = z.infer<typeof editCampaignFlowCodeRequestSchema>

export const createCampaignFlowCodeRequestSchema = z.object({
    clientId: z.number().int().positive(),
    emmCampaignFlowCode: z
        .object({
            campaign_flow_code_key: campaignFlowCodeKeySchema,
            campaign_flow_code: z.string().trim().min(1, 'Flow code is required'),
            version: campaignFlowVersionSchema,
            tagId: z.number().int().positive().optional(),
            createTag: z.boolean(),
        })
        .superRefine((value, ctx) => {
            if (value.createTag) return
            if (value.tagId == null) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Choose an existing tag or tick create tag',
                    path: ['tagId'],
                })
            }
        }),
})

export type CreateCampaignFlowCodeRequest = z.infer<typeof createCampaignFlowCodeRequestSchema>
